import React, { createContext, FC, JSX, useEffect, useState } from "react";
import { AdvancedExclusionsWizardContextSteps, AdvancedExclusionsWizardRouteState, BulkProjectionSharedContextProps } from "./interfaces";
import { BulkProviderChangeProjectTableData } from "@app/shared/provider-change-wizards/interfaces";
import ProjectionProviderService from "../projectionProviderService";
import { ProjectionProviderIdentifier } from "../projectionProviderDetailsReducer";
import ProjectService from "@app/projects/projectService";
import { useLocation } from "react-router-dom";
import { AccessPointForProjection } from "@app/projects/results/projection-provider";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { BulkProjectTypeEnum } from "@app/shared/enums/bulkProjectTypeEnum";

interface AdvancedExclusionsContextProps extends BulkProjectionSharedContextProps {
  allLocations: boolean;
  allSpecialties: boolean;
  comment: string;
  currentProjectId: string;
  error: string | JSX.Element;
  exclusionReason: string;
  exclusionsType: ProjectionProviderIdentifier;
  loading: boolean;
  page: AdvancedExclusionsWizardContextSteps;
  phone: string;
  providerNetworkType: ProviderNetworkTypeEnum;
  projectService: ProjectService;
  projectType: BulkProjectTypeEnum;
  runProjectionOnSubmit: boolean;
  selectedVerifiedSources: string[];
  setAllLocations: React.Dispatch<React.SetStateAction<boolean>>;
  setAllSpecialties: React.Dispatch<React.SetStateAction<boolean>>;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  setExclusionReason: React.Dispatch<React.SetStateAction<string>>;
  setExclusionsType: React.Dispatch<React.SetStateAction<ProjectionProviderIdentifier>>;
  setPage: React.Dispatch<React.SetStateAction<AdvancedExclusionsWizardContextSteps>>;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  setRunProjectionOnSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  selectedExclusionProviders: AccessPointForProjection[];
  setSelectedVerifiedSources: React.Dispatch<React.SetStateAction<string[]>>;
  specialtyGroup: string;

  updateSelectedProjects: (ids: string[]) => void;
}

export const AdvancedExclusionsContext = createContext<Partial<AdvancedExclusionsContextProps>>({});

const AdvancedExclusionsContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const routeState = useLocation<AdvancedExclusionsWizardRouteState>().state;

  const projectionProviderService = new ProjectionProviderService();
  const projectService = new ProjectService();

  const [page, setPage] = useState<AdvancedExclusionsWizardContextSteps>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | JSX.Element>("");

  //Step One: Select Projects
  const [currentProjectId] = useState<string>(routeState.currentProjectId);
  const [projects, setProjects] = useState<BulkProviderChangeProjectTableData[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [specialtyGroup] = useState<string>(routeState.specialtyGroup);
  const [projectType] = useState<BulkProjectTypeEnum>(routeState.projectType ?? BulkProjectTypeEnum.County);

  //Step Two: Verified Sources
  const [selectedVerifiedSources, setSelectedVerifiedSources] = useState<string[]>([]);

  //Step Three: Confirm
  const [allSpecialties, setAllSpecialties] = useState<boolean>(false);
  const [allLocations, setAllLocations] = useState<boolean>(false);
  const [runProjectionOnSubmit, setRunProjectionOnSubmit] = useState<boolean>(true);
  const [exclusionsType, setExclusionsType] = useState<ProjectionProviderIdentifier>(ProjectionProviderIdentifier.NPI);
  const [exclusionReason, setExclusionReason] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [selectedExclusionProviders] = useState<AccessPointForProjection[]>(routeState.selectedExclusionProviders);
  const [providerNetworkType] = useState<ProviderNetworkTypeEnum>(routeState.providerNetworkType);


  useEffect(() => {
    setPage(AdvancedExclusionsWizardContextSteps.SelectProject);
    // The default view has the current project selected.
    setSelectedProjectIds([currentProjectId]);
  }, []);

  useEffect(() => {
    setLoading(false);
    setError("");
  }, []);

  const updateSelectedProjects = (ids: string[]) => {
    setSelectedProjectIds(ids);
  }

  const contextValue: AdvancedExclusionsContextProps = {
    allLocations,
    allSpecialties,
    comment,
    currentProjectId,
    error,
    exclusionReason,
    exclusionsType,
    loading,
    page,
    phone,
    projectionProviderService,
    projects,
    projectService,
    projectType,
    providerNetworkType,
    runProjectionOnSubmit,
    selectedExclusionProviders,
    selectedProjectIds,
    selectedVerifiedSources,
    setAllLocations,
    setAllSpecialties,
    setComment,
    setExclusionReason,
    setExclusionsType,
    setPage,
    setPhone,
    setProjects,
    setRunProjectionOnSubmit,
    setSelectedProjectIds,
    setSelectedVerifiedSources,
    specialtyGroup,
    updateSelectedProjects
  };

  return (
    <AdvancedExclusionsContext.Provider value={contextValue}>
      {props.children}
    </AdvancedExclusionsContext.Provider>
  );
}

export default AdvancedExclusionsContextProvider;